<template>
  <div
    class="tw-flex tw-max-w-full tw-gap-1.5"
    :class="layoutClass"
    data-testid="assistant-suggestion-pills"
  >
    <button
      v-for="suggestion in pills"
      :key="suggestion.prompt"
      type="button"
      class="
        tw-max-w-full
        tw-min-w-0
        tw-rounded
        tw-border
        tw-border-solid
        tw-px-2
        tw-py-1
        tw-font-sans
        tw-text-xs
        tw-text-left
        tw-bg-transparent
        tw-cursor-pointer
        hover:tw-bg-sf-slate-subtle-1
      "
      :class="
        variant === 'reply'
          ? 'tw-border-sf-ai-lighter-1 tw-text-sf-ai-darker-1'
          : 'tw-border-sf-slate-lighter-1 tw-text-sf-slate'
      "
      data-testid="assistant-suggestion-pill"
      @click="$emit('select', suggestion.prompt)"
    >
      <SfTruncatedLabel :text="suggestion.tagline" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { SuggestedPrompt } from '@app/insights/store/suggested-prompts'
import SfTruncatedLabel from '@app/shared/vue/components/sf-truncated-label.vue'

interface Props {
  // Empty-state prompts carry a short tagline for the pill. Reply follow-ups are plain
  // sentences, and label the pill with themselves.
  suggestions: (SuggestedPrompt | string)[]
  // 'empty' = neutral pills in the empty state (paste into input on click);
  // 'reply' = violet AI pills under a reply (submit on click);
  // 'card' = neutral pills in the queries overview card (opens a new query on click).
  variant: 'empty' | 'reply' | 'card'
}

const props = defineProps<Props>()
defineEmits<{ (e: 'select', prompt: string): void }>()

const layoutClass = computed((): string => {
  if (props.variant === 'reply') return 'tw-flex-col tw-items-start'
  if (props.variant === 'card') return 'tw-flex-wrap'
  return 'tw-flex-wrap tw-justify-center'
})

const pills = computed((): SuggestedPrompt[] =>
  props.suggestions.map((suggestion) =>
    typeof suggestion === 'string'
      ? { tagline: suggestion, prompt: suggestion }
      : suggestion
  )
)
</script>
